/* One contract, in full.
 *
 * The leveraged book showed a card per open contract and a single line per
 * closed one: name, side, leverage, date, result. Everything that made the
 * trade what it was — what it filled at, what it was liquidated against, how
 * much was posted against it, what was taken off along the way — was recorded
 * and never shown again. A result with no figures behind it is a number you
 * either believe or do not; there is nothing to check it against.
 *
 * So every figure the book holds about a contract is here, and the ones it does
 * not hold say so rather than being filled in. A contract entered before the
 * open date was recorded has no open date, and the screen prints that instead
 * of subtracting its way to a plausible number of days. */

import { moneyExact, moneyExactSigned, pct, pctSigned, price as fmtPrice, shortDate } from '../lib/format';
import { PerpActions } from '../components/AdjustPerp';
import { HideToggle } from '../components/HideToggle';
import { derivePerps } from '../state/derive';
import { useDisplay } from '../state/display';
import { useStore } from '../state/store';
import type { Nav } from '../App';

const DAY = 86_400_000;

export function ContractDetail({ id, nav }: { id: string; nav: Nav }) {
  const { state } = useStore();
  const { currency, toDisplay } = useDisplay();

  const perp = state.data.perps.find((p) => p.id === id);

  if (!perp) {
    return (
      <div className="screen">
        <div className="screen-head">
          <button className="back" onClick={nav.back} aria-label="Back">‹</button>
          <h1>Contract</h1>
          <HideToggle push />
        </div>
        <div className="empty">This contract is no longer in the book.</div>
      </div>
    );
  }

  const closed = !!perp.closedAt;
  /* The live figures come from the same derivation the leveraged book uses, so
     the two screens cannot drift. A closed contract is marked to nothing: its
     margin went back to the wallet and its result is history. */
  const view = derivePerps([perp], state.perpMarks)[0]!;

  const heldDays = perp.openedAt && perp.closedAt
    ? Math.max(0, Math.round((new Date(perp.closedAt).getTime() - new Date(perp.openedAt).getTime()) / DAY))
    : perp.openedAt
      ? Math.max(0, Math.round((Date.now() - new Date(perp.openedAt).getTime()) / DAY))
      : null;

  const result = perp.closedResultUsdt ?? 0;
  /* Older closes wiped the size on the way out. Absent is not zero. */
  const sizeKnown = perp.sizeUsdt > 0 && perp.entryPrice > 0;
  const onMargin = perp.marginUsdt > 0 ? (view.unrealisedUsd / perp.marginUsdt) * 100 : 0;

  const Row = ({ k, v, tone }: { k: string; v: string; tone?: 'up' | 'down' | 'crit' }) => (
    <div className="kv">
      <span className="k">{k}</span>
      <span className={`v num${tone ? ` ${tone}` : ''}`}>{v}</span>
    </div>
  );

  return (
    <div className="screen">
      <div className="screen-head">
        <button className="back" onClick={nav.back} aria-label="Back">‹</button>
        <h1>{perp.display}</h1>
        <span className="meta" style={{ marginLeft: 'auto' }}>{perp.symbol}</span>
        <HideToggle />
      </div>

      <div className="card hero">
        <div className="hero-label">
          {closed ? `Closed ${shortDate(perp.closedAt!)}` : 'Open'}
        </div>
        <div className={`hero-total num ${(closed ? result : view.unrealisedUsd) >= 0 ? 'up' : 'down'}`}>
          {moneyExactSigned(toDisplay(closed ? result : view.unrealisedUsd), currency)}
        </div>
        <div className="hero-change">
          <span className="perp-side" data-side={perp.side}>
            {perp.side.toUpperCase()} {perp.leverage}x
          </span>
          {!closed ? (
            <span className={`num ${onMargin >= 0 ? 'up' : 'down'}`}>{pctSigned(onMargin)} on margin</span>
          ) : null}
          {view.markStale && !closed ? <span className="tag" data-kind="muted">NO MARK</span> : null}
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
          {closed
            ? 'What it finally came to, typed in from the exchange rather than taken from the last '
              + 'mark. The fill is what happened, the mark is only where the screen had got to.'
            : 'Unrealised, against the entry price below. It becomes a result when you close it '
              + 'and tell the book what the exchange gave you.'}
        </div>
      </div>

      <section>
        <div className="section-head"><h2>How it was entered</h2></div>
        <div className="card card-pad">
          <Row k="Entry price" v={fmtPrice(perp.entryPrice)} />
          <Row k={perp.liqEstimated ? 'Liquidation, estimated' : 'Liquidation'}
               v={`${perp.liqEstimated ? '≈ ' : ''}${fmtPrice(perp.liqPrice)}`} tone="crit" />
          <Row k="Take profit" v={perp.takeProfit ? fmtPrice(perp.takeProfit) : 'none set'} />
          <div className="divider" />
          {/* A contract closed by an older version had these zeroed on the way
              out, and a zero here would read as "nothing was posted" rather
              than "the book no longer has it". */}
          <Row k="Margin posted"
               v={sizeKnown ? `${moneyExact(perp.marginUsdt)} USDT` : 'not kept on closing'} />
          <Row k="Notional at entry"
               v={sizeKnown ? `${moneyExact(perp.sizeUsdt)} USDT` : 'not kept on closing'} />
          <Row k="Contracts"
               v={sizeKnown ? (perp.sizeUsdt / perp.entryPrice).toFixed(6) : '—'} />
          <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
            {sizeKnown
              ? 'The notional is margin times leverage, which is what the position was worth when it was '
                + 'opened. It is deliberately not the “Size (USDT)” figure on the exchange card, '
                + 'which is contracts at the current mark and drifts every tick.'
              : 'This contract was closed by a version that cleared its size on the way out, so how '
                + 'big it was is no longer in the book. It is left blank rather than shown as zero. '
                + 'Contracts closed from now on keep these figures.'}
          </div>
        </div>
      </section>

      {!closed ? (
        <section>
          <div className="section-head"><h2>Where it stands</h2></div>
          <div className="card card-pad">
            <Row k="Mark" v={view.mark === null ? 'no mark' : fmtPrice(view.mark)} />
            <Row k="Notional now" v={`${moneyExact(view.notionalNowUsd)} USDT`} />
            <Row k="To liquidation" v={view.mark === null ? '—' : pct(view.distanceToLiqPct)}
                 tone={view.distanceToLiqPct < 15 ? 'crit' : undefined} />
            {perp.autoMarginAddition ? (
              <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
                Auto margin addition is on, so a move against this pulls from your spare cash before
                it liquidates. The liquidation price is where it stands today, not where it stays.
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section>
        <div className="section-head"><h2>What it did</h2></div>
        <div className="card card-pad">
          {typeof perp.realizedUsdt === 'number' && perp.realizedUsdt !== 0 ? (
            <Row k="Taken off along the way" v={moneyExactSigned(perp.realizedUsdt)}
                 tone={perp.realizedUsdt >= 0 ? 'up' : 'down'} />
          ) : null}
          {closed ? (
            <Row k="Result on closing" v={moneyExactSigned(result)} tone={result >= 0 ? 'up' : 'down'} />
          ) : null}
          <Row k="Opened" v={perp.openedAt ? shortDate(perp.openedAt) : 'not recorded'} />
          {closed ? <Row k="Closed" v={shortDate(perp.closedAt!)} /> : null}
          <Row k="Held" v={heldDays === null ? 'not known' : `${heldDays} days${closed ? '' : ' so far'}`} />

          <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
            {perp.openedAt
              ? null
              : 'This contract was entered before the book recorded open dates, so how long it ran '
                + 'is not something it knows. It is left blank rather than worked out from a date '
                + 'nobody wrote down.'}
            {typeof perp.realizedUsdt === 'number' && perp.realizedUsdt !== 0 && closed ? (
              ' The result on closing includes what was taken off in parts before it.'
            ) : null}
          </div>
        </div>
      </section>

      {/* Shown on a closed contract too. It used to disappear the moment one
          closed, which is exactly when a wrong figure in that close has to be
          put right: there was no way back to a contract closed out by mistake
          or at the wrong number. */}
      <section>
        <div className="section-head"><h2>{closed ? 'Change the record' : 'Change it'}</h2></div>
        <div className="card card-pad">
          <PerpActions perp={view} say={() => { /* the card reports it itself */ }} />
          {closed ? (
            <div className="hint" style={{ marginTop: 10 }}>
              This contract is closed. Correcting it restates what the book says about it;
              undoing the close puts the margin, the notional and the money back and the
              contract counts as open again.
            </div>
          ) : null}
        </div>
      </section>

      <p className="dim" style={{ fontSize: 11, textAlign: 'center', marginTop: 24, lineHeight: 1.5 }}>
        Marks come from the exchange. Everything else here you entered. The app holds no exchange
        keys and places no orders.
      </p>
    </div>
  );
}
